'use client'

import Link from 'next/link'
import { Button } from '../ui/button'
import { LinkIcon } from 'lucide-react'

import { convertLexicalToPlaintext } from '@payloadcms/richtext-lexical/plaintext'

import { Media } from '@/components/Media'
import { cn } from '@/utilities/ui'

import type { Organization } from '@/payload-types'
import type { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical'

const getDomainFromURL = (websiteUrl: string) => {
  try {
    const { hostname } = new URL(websiteUrl)
    const parts = hostname.replace(/^www\./, '').split('.')

    if (parts.length <= 2) {
      return parts.join('.')
    }

    return parts.slice(-3).join('.')
  } catch {
    return websiteUrl
  }
}

export const OrgCard: React.FC<{
  alignItems?: 'center'
  className?: string
  doc?: Organization
  relationTo?: 'organizations'
  title?: string
  forceWidth?: boolean
  onClick?: () => void
}> = (props) => {
  // const { className, doc, relationTo, title: title, onClick, forceWidth } = props
  const { className, doc, title: title, onClick, forceWidth } = props

  const {
    // slug,
    shortDescription,
    logoImage,
    websiteUrl,
    heroImage,
    organizationDescription,
    backupImage,
  } = doc || {}

  const titleToUse = title || doc?.title

  // display only the domain & tld (e.g. google.com)
  const websiteUrlToUse = getDomainFromURL(websiteUrl || '')
  const plaintext = convertLexicalToPlaintext({
    data: organizationDescription as SerializedEditorState,
  })

  const heroImageToUse = heroImage && typeof heroImage !== 'string' ? heroImage : backupImage

  return (
    <article
      className={cn(
        'flex min-h-115 flex-col overflow-hidden rounded-2xl bg-card shadow-border',
        forceWidth ? 'w-full max-w-105 min-w-105' : 'w-full',
        className,
      )}
    >
      <div className="relative m-2">
        <div className="relative h-39 w-full overflow-hidden rounded-md">
          {heroImageToUse && (
            <Media
              resource={heroImageToUse}
              context="thumbnail"
              pictureClassName="visible absolute inset-0 block size-full overflow-hidden object-cover opacity-100 transition-all duration-500 ease-in"
              imgClassName="size-full object-cover object-center"
            />
          )}
        </div>
        <div className="absolute -bottom-8 left-4 size-16 overflow-hidden rounded-xl bg-card">
          {!logoImage && <div className="">No image</div>}
          {logoImage && typeof logoImage !== 'string' && (
            <Media
              resource={logoImage}
              context="thumbnail"
              htmlelement={null}
              pictureClassName="size-full rounded-xl object-cover object-cover"
              imgClassName="size-full rounded-xl object-cover"
            />
          )}
        </div>
      </div>
      <div className="flex grow flex-col p-6">
        <h3 className="my-2 font-display text-2xl leading-8 font-normal text-foreground">
          {titleToUse}
        </h3>
        <div className="flex grow flex-col">
          {shortDescription && (
            <div className="mt-2 max-h-18 shrink">
              <p className="line-clamp-3 font-family-body leading-6 text-ellipsis text-card-foreground">
                {shortDescription}
              </p>
            </div>
          )}
          {!shortDescription && organizationDescription && (
            <div className="mt-2 max-h-18 shrink">
              <p className="line-clamp-3 font-family-body leading-6 text-ellipsis text-card-foreground">
                {plaintext.slice(0, 200)}
                {plaintext.length > 200 && '...'}
              </p>
            </div>
          )}
          <div className="mt-auto flex flex-wrap justify-between gap-2 pt-6">
            <Button variant="link" onClick={onClick}>
              Read More<span className="sr-only">{` about ${titleToUse}`}</span>
            </Button>
            {websiteUrl && (
              <Link
                href={websiteUrl}
                target="_blank"
                className="line-clamp-1 flex items-center gap-1 truncate font-body text-sm font-medium text-neutral-500"
              >
                <LinkIcon className="size-4 shrink-0 text-neutral-400" />
                <span className="sr-only">{`Visit ${titleToUse}`} at </span>
                {websiteUrlToUse}
              </Link>
            )}
          </div>
        </div>
      </div>
    </article>
  )
}
